import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Skeleton, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MultipleSubImage } from '../../../components/SubImage';
import useIsMobile from '../../../hooks/isMobile';
import imageNA from '../../../images/image_not_available.png';
import { VideoStreamType } from '../../../utils/ImageDimensions';
import palette from '../../ColorPalette';
import { RTLDirectionContext } from '../../Layout';

interface SegmentImagesProps {
    snapshotStart?: string;
    snapshotEnd?: string;
}

const SegmentImages: React.FC<SegmentImagesProps> = ({ snapshotStart, snapshotEnd }: SegmentImagesProps) => {
    const { t } = useTranslation();
    const isRTL = useContext(RTLDirectionContext);
    const isMobile = useIsMobile();
    const [primaryStream, setPrimaryStream] = useState<VideoStreamType | null>(null);

    const placeholder = (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Typography variant="overline" color="primary">
                {t('content.find_video.load_images')}
            </Typography>
        </Box>
    );
    const skeleton = <Skeleton variant="rectangular" animation="wave" width="100%" height="100%" />;
    const noImage = <img src={imageNA} width="100%" height="100%" />;

    const renderImage = (src: string) => (
        <MultipleSubImage
            image={src}
            containerStyle={{ width: '100%', height: '100%' }}
            primaryStream={primaryStream}
            onPrimaryStreamChange={setPrimaryStream}
            showOverlays={true}
            enableStreamSwapping={true}
        />
    );

    let start;
    if (snapshotStart !== undefined) {
        if (snapshotStart === 'no snapshot') {
            start = noImage;
        } else if (snapshotStart === 'loading') {
            start = skeleton;
        } else {
            start = renderImage(snapshotStart);
        }
    } else {
        start = placeholder;
    }

    let end;
    if (snapshotEnd !== undefined) {
        if (snapshotEnd === 'no snapshot') {
            end = noImage;
        } else if (snapshotEnd === 'loading') {
            end = skeleton;
        } else {
            end = renderImage(snapshotEnd);
        }
    } else {
        end = placeholder;
    }

    let arrowIcon;
    if (isMobile) {
        arrowIcon = <KeyboardArrowDownIcon color="primary" sx={{ fontSize: 40, mx: 1 }} />;
    } else {
        if (isRTL) {
            arrowIcon = <ArrowBackIosNewIcon color="primary" sx={{ mx: 1 }} />;
        } else {
            arrowIcon = <ArrowForwardIosIcon color="primary" sx={{ mx: 1 }} />;
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', width: '100%' }}>
            <Box
                id="start-images"
                style={{
                    width: '100%',
                    display: 'flex',
                    aspectRatio: start === skeleton || start === placeholder ? 8 / 3 : undefined,
                    outline: isMobile ? undefined : `1px solid ${palette.primary}`,
                }}
            >
                {start}
            </Box>
            {arrowIcon}
            <Box
                id="end-images"
                style={{
                    width: '100%',
                    display: 'flex',
                    aspectRatio: end === skeleton || end === placeholder ? 8 / 3 : undefined,
                    outline: isMobile ? undefined : `1px solid ${palette.primary}`,
                }}
            >
                {end}
            </Box>
        </Box>
    );
};

export default SegmentImages;
